/**
 * El registro de lo que se ha tocado en la agenda (spec 015 · addendum).
 *
 * La primera pregunta cuando una cita aparece movida o desaparecida es **quién y
 * cuándo**. Hasta hoy la respuesta estaba solo en `psql`.
 *
 * El asistente también (spec 030). El aviso solo sale si no se pudo leer
 * su rastro — no si está vacío.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { api } from '../../lib/api'
import { BotonVaciarAuditoria } from './vaciar-auditoria'

interface Movimiento {
    id: number
    cuando: string
    quien: string | null
    accion: string
    recurso: string
    recursoEaId: string | null
    resultado: string
    motivo: string | null
    cambio: { antes?: Record<string, unknown>; despues?: Record<string, unknown> } | null
}

interface Respuesta {
    accountId: number
    incluyeBot: boolean
    desde: string | null
    hasta: string | null
    hayMas: boolean
    movimientos: Movimiento[]
}

/**
 * Los nombres de profesionales y servicios, para no enseñar «profesional 4».
 *
 * Se piden APARTE y el registro se pinta igual si no llegan.
 *
 * Y eso no es pereza: el registro es de la base del panel, pero los nombres
 * están en la agenda. Un registro que necesitara la agenda para poder leerse
 * fallaría **justo cuando más se le necesita** — cuando algo va mal—. Así que si
 * la agenda no responde, se enseña el número y ya está.
 */
interface Catalogo {
    profesionales: { id: number; nombre: string; color: string }[]
    servicios: { id: number; nombre: string }[]
}



/**
 * El color de cada clase de acción.
 *
 * No es decoración: en un listado de cuarenta filas, el color es lo que deja
 * distinguir de un vistazo **una cancelación** de un cambio de hora. Leerlas una
 * a una para eso es justo lo que hace que un registro no se use.
 *
 * Se evita el ámbar a propósito: en este panel ya significa «de prueba» y
 * «aviso», y aquí lo usa el rechazo.
 */
const TONO: Record<string, { texto: string; fondo: string; etiqueta: string }> = {
    'cita.crear': { texto: 'text-emerald-300', fondo: 'bg-emerald-500/10', etiqueta: 'Creada' },
    'cita.mover': { texto: 'text-sky-300', fondo: 'bg-sky-500/10', etiqueta: 'Movida' },
    'cita.editar': { texto: 'text-sky-300', fondo: 'bg-sky-500/10', etiqueta: 'Editada' },
    'cita.cancelar': { texto: 'text-rose-300', fondo: 'bg-rose-500/10', etiqueta: 'Cancelada' },
    'profesional.editar': { texto: 'text-violet-300', fondo: 'bg-violet-500/10', etiqueta: 'Profesional' },
    'horario.editar': { texto: 'text-violet-300', fondo: 'bg-violet-500/10', etiqueta: 'Horario' },
    'vacacion.crear': { texto: 'text-violet-300', fondo: 'bg-violet-500/10', etiqueta: 'Vacaciones' },
    'vacacion.borrar': { texto: 'text-violet-300', fondo: 'bg-violet-500/10', etiqueta: 'Vacaciones' },
}

const TONO_POR_DEFECTO = { texto: 'text-neutral-300', fondo: 'bg-neutral-800', etiqueta: 'Cambio' }

/**
 * Un extremo del movimiento: de dónde venía, o dónde quedó.
 *
 * Se compone la MISMA pieza para las dos columnas, y eso importa: si origen y
 * destino se pintaran con código distinto, acabarían enseñando cosas distintas
 * —una con el profesional y otra sin él— y comparar las dos columnas, que es
 * para lo único que sirve tenerlas, dejaría de funcionar.
 */
function Extremo({
    campos,
    cat,
    accountId,
    vacio,
}: {
    campos: Record<string, unknown> | undefined
    cat: Catalogo | undefined
    accountId: string | undefined
    /** Qué poner cuando no hay nada que enseñar. */
    vacio: React.ReactNode
}) {
    if (!campos || Object.keys(campos).length === 0) return <>{vacio}</>

    // Se intentó leer qué se borraba y no se pudo. Distinto de «no se apuntó».
    if (campos['noSePudoLeer'] === true) {
        return (
            <span className="text-amber-400" title="Se canceló, pero no se pudo leer qué era antes de borrarlo">
                no se pudo leer
            </span>
        )
    }

    const prof =
        campos['profesionalId'] !== undefined
            ? (cat?.profesionales.find((p) => p.id === campos['profesionalId'])?.nombre ??
              `profesional ${String(campos['profesionalId'])}`)
            : null
    const serv =
        campos['servicioId'] !== undefined
            ? (cat?.servicios.find((x) => x.id === campos['servicioId'])?.nombre ??
              `servicio ${String(campos['servicioId'])}`)
            : null
    const color =
        campos['profesionalId'] !== undefined
            ? cat?.profesionales.find((p) => p.id === campos['profesionalId'])?.color
            : undefined

    return (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {/* La hora ENLAZA al calendario de ese día: es la pregunta que sigue
                a «¿qué pasó?» — «¿y cómo quedó ese día?». */}
            {campos['inicio'] !== undefined &&
                (accountId && diaDe(campos['inicio']) ? (
                    <Link
                        to={`/business/${accountId}/calendar?dia=${diaDe(campos['inicio'])}`}
                        className="tabular-nums text-neutral-100 underline decoration-dotted underline-offset-2 hover:text-sky-300"
                        title="Ver el calendario de ese día"
                    >
                        {cuandoLargo(campos['inicio'])}
                    </Link>
                ) : (
                    <span className="tabular-nums text-neutral-100">{cuandoLargo(campos['inicio'])}</span>
                ))}
            {prof && (
                <span className="flex items-center gap-1 text-neutral-400">
                    {color && (
                        <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: color }}
                        />
                    )}
                    {prof}
                </span>
            )}
            {serv && <span className="text-neutral-500">{serv}</span>}

            {/* La persona, por su IDENTIFICADOR y nunca por su nombre.
                El registro no guarda datos de paciente a propósito: su tabla está
                excluida de la retención, así que lo que se escriba ahí sobrevive
                a un borrado por derecho de supresión. Con el id se llega igual,
                porque el contacto del canal lo lleva. */}
            {campos['clienteId'] !== undefined && (
                <span className="text-neutral-500" title="Su ficha en la agenda">
                    cliente nº {String(campos['clienteId'])}
                </span>
            )}
        </span>
    )
}

/** `2026-08-20 13:00:00` → `2026-08-20`. Para enlazar al calendario. */
function diaDe(v: unknown): string | null {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v))
    return m?.[1] ?? null
}

/** `2026-08-20 13:00:00` → `jue 20 ago · 13:00`. */
function cuandoLargo(v: unknown): string {
    const s = String(v)
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}:\d{2})/.exec(s)
    if (!m) return s
    const dia = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00Z`).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    })
    return `${dia} · ${m[4]}`
}

/** `YYYY-MM-DD` de hace N días. Para los atajos del rango. */
function haceDias(n: number): string {
    return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10)
}

/** «Hoy», «Ayer», o la fecha larga. Leer `2026-08-18` cuesta más que leer «hoy». */
function tituloDia(yyyymmdd: string): string {
    const hoy = new Date().toISOString().slice(0, 10)
    const ayer = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)
    if (yyyymmdd === hoy) return 'Hoy'
    if (yyyymmdd === ayer) return 'Ayer'
    return new Date(`${yyyymmdd}T12:00:00Z`).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    })
}

export function AuditoriaNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    // Sin rango se enseña lo último, que es lo que se quiere al abrir. El rango
    // es para buscar algo concreto: «qué pasó la semana del 10».
    const [desde, setDesde] = useState('')
    const [hasta, setHasta] = useState('')

    const qs = new URLSearchParams()
    if (desde) qs.set('desde', desde)
    if (hasta) qs.set('hasta', hasta)
    const cola = qs.toString() ? `?${qs.toString()}` : ''

    const qc = useQueryClient()
    const { data, isPending, isError } = useQuery<Respuesta>({
        queryKey: ['auditoria', accountId, desde, hasta],
        queryFn: () => api.get<Respuesta>(`/business/${accountId}/auditoria${cola}`),
        placeholderData: (previa) => previa,
    })
    const [avisoVaciar, setAvisoVaciar] = useState<string | null>(null)
    const vaciar = useMutation({
        mutationFn: () =>
            api.delete<{ borrados: number; rastroVaciado: boolean }>(`/business/${accountId}/auditoria`),
        onSuccess: (r) => {
            void qc.invalidateQueries({ queryKey: ['auditoria', accountId] })
            setAvisoVaciar(
                r.rastroVaciado
                    ? null
                    : 'El registro del panel se vació. Lo del asistente no se pudo borrar.',
            )
        },
        onError: () => setAvisoVaciar('No se pudo vaciar el registro.'),
    })

    // Los nombres, aparte y sin bloquear. Si la agenda no responde, el registro
    // se lee igual con los números — ver el comentario de `Catalogo`.
    const { data: catalogo } = useQuery<Catalogo>({
        queryKey: ['auditoria-catalogo', accountId],
        queryFn: () => api.get<Catalogo>(`/business/${accountId}/calendar`),
        retry: false,
        staleTime: 5 * 60_000,
    })

    // Agrupado por día: un listado plano de cuarenta filas obliga a leer la fecha
    // en cada una para saber si sigue siendo hoy.
    const porDia = new Map<string, Movimiento[]>()
    for (const m of data?.movimientos ?? []) {
        // El día TAMBIÉN en local: entre las 00:00 y las 02:00 de aquí, cortar el
        // ISO en UTC metía los movimientos en el día anterior.
        const f = new Date(m.cuando)
        const d = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`
        const lista = porDia.get(d)
        if (lista) lista.push(m)
        else porDia.set(d, [m])
    }

    return (
        <div className="space-y-4">
            <header className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-neutral-100">Movimientos de la agenda</h2>
                    <p className="mt-1 text-sm text-neutral-400">
                        Quién tocó qué, cuándo, y de dónde a dónde.
                    </p>
                </div>
                <BotonVaciarAuditoria
                    vaciando={vaciar.isPending}
                    vacio={(data?.movimientos.length ?? 0) === 0 && !avisoVaciar}
                    alVaciar={() => {
                        setAvisoVaciar(null)
                        vaciar.mutate()
                    }}
                />
            </header>
            {avisoVaciar && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                    {avisoVaciar}
                </p>
            )}

            {!data?.incluyeBot && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm leading-relaxed text-amber-200">
                    No se pudo leer lo que hizo el asistente. Lo del panel sí está. Un registro que
                    parece completo y no lo es lleva a concluir que nadie tocó la agenda.
                </p>
            )}

            {/* El rango. Los atajos primero porque son lo que se usa el 90 % de
                las veces; las dos fechas, para lo demás. */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900/40 p-3 text-sm">
                {(
                    [
                        ['', 'Lo último'],
                        ['7', 'Últimos 7 días'],
                        ['30', 'Últimos 30 días'],
                    ] as const
                ).map(([dias, et]) => {
                    const d = dias ? haceDias(Number(dias)) : ''
                    const activo = desde === d && hasta === ''
                    return (
                        <button
                            key={et}
                            onClick={() => {
                                setDesde(d)
                                setHasta('')
                            }}
                            aria-pressed={activo}
                            className={
                                'rounded-md border px-3 py-1 transition ' +
                                (activo
                                    ? 'border-neutral-600 bg-neutral-800 text-neutral-100'
                                    : 'border-neutral-800 text-neutral-500 hover:text-neutral-300')
                            }
                        >
                            {et}
                        </button>
                    )
                })}

                <span className="ml-2 text-neutral-600">o entre fechas</span>
                <input
                    type="date"
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                    aria-label="Desde"
                    className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-neutral-200"
                />
                <span className="text-neutral-600">→</span>
                <input
                    type="date"
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                    aria-label="Hasta"
                    className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-neutral-200"
                />
                {(desde || hasta) && (
                    <button
                        onClick={() => {
                            setDesde('')
                            setHasta('')
                        }}
                        className="text-neutral-500 underline decoration-dotted hover:text-neutral-300"
                    >
                        quitar
                    </button>
                )}
            </div>

            {/* Que se ha llegado al tope SE DICE. Un listado cortado en silencio
                se lee como «esto es todo lo que pasó», que es lo contrario. */}
            {data?.hayMas && (
                <p className="text-sm text-amber-300">
                    Hay más movimientos de los que caben. Acota el rango para verlos todos.
                </p>
            )}

            {isPending && <p className="text-base text-neutral-500">Leyendo el registro…</p>}
            {isError && <p className="text-sm text-rose-400">No se pudo leer el registro.</p>}

            {data && data.movimientos.length === 0 && (
                <p className="rounded-lg border border-dashed border-neutral-800 p-8 text-center text-base text-neutral-400">
                    Todavía no se ha tocado nada de esta agenda desde el panel.
                </p>
            )}

            {data && data.movimientos.length > 0 && (
                <div className="space-y-5">
                    {[...porDia.entries()].map(([dia, lista]) => (
                        <section key={dia}>
                            <h3 className="mb-2 text-sm font-medium text-neutral-400">
                                {tituloDia(dia)}
                                <span className="ml-2 text-neutral-600">
                                    {lista.length} movimiento{lista.length > 1 ? 's' : ''}
                                </span>
                            </h3>

                            <div className="overflow-x-auto rounded-lg border border-neutral-800">
                                <table className="tabla-carta w-full text-[15px]">
                                    <thead>
                                        <tr className="border-b border-neutral-800 text-left text-xs uppercase tracking-wide text-neutral-500">
                                            <th className="px-4 py-2 font-medium">Hora</th>
                                            <th className="px-4 py-2 font-medium">Qué</th>
                                            <th className="px-4 py-2 font-medium">Origen</th>
                                            <th className="px-4 py-2 font-medium">Destino</th>
                                            <th className="px-4 py-2 font-medium">Quién</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lista.map((m) => {
                                            const tono = TONO[m.accion] ?? TONO_POR_DEFECTO
                                            const rechazado = m.resultado !== 'ok'
                                            const esCancelar = m.accion === 'cita.cancelar'
                                            // Las filas de antes del 2026-08-18 no guardaban el
                                            // detalle. Se dice: dos guiones se leen como un fallo
                                            // de la pantalla, y no lo son.
                                            const sinDetalle = !m.cambio
                                            return (
                                                <tr
                                                    key={m.id}
                                                    className="border-b border-neutral-900 align-top last:border-b-0"
                                                >
                                                    {/* HORA LOCAL, no UTC. `cuando` es
                                                        un ISO en UTC y cortarlo a
                                                        pelo enseñaba las 05:25 de un
                                                        movimiento hecho a las 07:25 en
                                                        Madrid. Quien busca «quién movió
                                                        la cita a las 7:25» no encontraba
                                                        la fila. */}
                                                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-neutral-500" data-label="Hora">
                                                        {new Date(m.cuando).toLocaleTimeString('es-ES', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-3" data-label="Qué">
                                                        {/* Un intento fallido NO es la misma clase de
                                                            cosa que un cambio: no pasó nada. Se marca en
                                                            la propia etiqueta —tachada y en gris— para
                                                            que se distinga de un vistazo, sin leer. */}
                                                        <span
                                                            className={
                                                                'rounded px-2 py-0.5 text-sm ' +
                                                                (rechazado
                                                                    ? 'bg-neutral-800 text-neutral-500 line-through'
                                                                    : `${tono.fondo} ${tono.texto}`)
                                                            }
                                                        >
                                                            {tono.etiqueta}
                                                        </span>
                                                        {/* El rechazo se dice AQUÍ, junto a la acción:
                                                            es lo primero que cambia cómo se lee la fila. */}
                                                        {rechazado && (
                                                            <div className="mt-1 text-xs text-amber-300">
                                                                solo se intentó
                                                                {m.motivo === 'fuera_horario' && ' · fuera de horario'}
                                                                {m.motivo === 'solape' && ' · el hueco estaba ocupado'}
                                                                {m.motivo &&
                                                                    m.motivo !== 'fuera_horario' &&
                                                                    m.motivo !== 'solape' &&
                                                                    ` · ${m.motivo}`}
                                                            </div>
                                                        )}
                                                    </td>

                                                    {sinDetalle ? (
                                                        <td className="px-4 py-3 text-sm text-neutral-600" colSpan={2} data-label="Detalle">
                                                            se apuntó antes de que el registro guardara el
                                                            detalle
                                                        </td>
                                                    ) : (
                                                        <>
                                                    <td className="px-4 py-3" data-label="Origen">
                                                        <Extremo
                                                            campos={m.cambio?.antes}
                                                            cat={catalogo}
                                                            accountId={accountId}
                                                            vacio={
                                                                <span className="text-neutral-600">
                                                                    {m.accion === 'cita.crear' ? 'no existía' : '—'}
                                                                </span>
                                                            }
                                                        />
                                                    </td>

                                                    <td className="px-4 py-3" data-label="Destino">
                                                        {/* Una cancelación no tiene destino: TIENE BORRADO.
                                                            Dejar la celda vacía se leería como «no se sabe»,
                                                            que es lo contrario de lo que pasó. */}
                                                        {esCancelar && !rechazado ? (
                                                            <span className="rounded bg-rose-500/10 px-2 py-0.5 text-sm text-rose-300">
                                                                Borrada
                                                            </span>
                                                        ) : (
                                                            <Extremo
                                                                campos={m.cambio?.despues}
                                                                cat={catalogo}
                                                                accountId={accountId}
                                                                vacio={
                                                                    <span className="text-neutral-600">
                                                                        {rechazado ? 'no llegó a cambiar' : '—'}
                                                                    </span>
                                                                }
                                                            />
                                                        )}
                                                    </td>
                                                        </>
                                                    )}

                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-neutral-500" data-label="Quién">
                                                        {m.quien ?? (
                                                            <span title="Sin usuario: no lo hizo una persona">
                                                                el sistema
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ))}
                </div>
            )}

            <p className="text-sm text-neutral-500">
                Los intentos rechazados también se apuntan: saber que alguien intentó mover una cita a
                un hueco ocupado explica por qué esa cita sigue donde estaba.
            </p>
        </div>
    )
}
